import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems, MenuSeparator } from '@headlessui/react';
import { ChevronLeft, GraduationCap, Lightbulb, LogOut, MessageSquare, MessageSquarePlus, Search, Settings, WifiOff } from 'lucide-react';
import { IconButton, Tabs } from '../ui';
import { cx } from '../ui/cx';
import { findLocation } from './navigation';
import { iconFor } from './icons';
import GlobalSearch from './GlobalSearch';
import { PageTip, useOnboarding } from '../onboarding/OnboardingProvider';
import styles from './ModernShell.module.css';

const displayName = (user) => (user?.firstname && user?.lastname ? `${user.firstname} ${user.lastname}` : user?.username || '');
const initials = (user) => (user?.firstname?.[0] || user?.username?.[0] || '?').toUpperCase();
const ROLE_LABELS = { admin: 'Administrator', 'key-technician': 'Key-Techniker', technician: 'Techniker', technican: 'Techniker', dispatcher: 'Disposition', readonly: 'Nur lesen' };

/**
 * Oberfläche des neuen Designs: 5 Bereiche – unten auf dem Handy, links ab Tablet.
 * Unterseiten eines Bereichs erscheinen als Reiter, die Verwaltung als Kachel-Übersicht.
 */
export default function ModernShell({
  user, navigation, isOnline, badges = {}, appVersion, statusBar, devNotice, footerExtra,
  onLogout, onFeedback, onSuggestion, onCopyrightClick, onBrandClick, children
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { startTour } = useOnboarding();
  const [searchOpen, setSearchOpen] = useState(false);

  const current = findLocation(navigation, location.pathname);
  const area = current?.area;
  const showTabs = area && !area.hub && area.items.length > 1;
  const inAdminSubpage = area?.hub && current.item;

  // Strg+K / Cmd+K öffnet die Suche
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const areaBadge = (a) => a.items.reduce((sum, item) => sum + (badges[item.path] || 0), 0);
  const title = current?.item?.label || area?.label || (location.pathname === '/settings' ? 'Einstellungen' : 'Material-App');

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar} aria-label="Hauptnavigation">
        <Link to="/" className={styles.brand}>
          <span className={styles.logo} aria-hidden>🔧</span>
          <span className={styles.brandText}>
            <strong>Material-App</strong>
            <small>Ersatzteillogistik</small>
          </span>
        </Link>
        <nav className={styles.sideNav}>
          {navigation.map((a) => {
            const Icon = iconFor(a.icon);
            const active = a.key === area?.key;
            const badge = areaBadge(a);
            return (
              <div key={a.key} className={styles.sideGroup}>
                <Link to={a.path} className={cx(styles.sideLink, active && styles.sideActive)} aria-current={active ? 'page' : undefined}>
                  <span className={styles.sideIcon}>
                    <Icon aria-hidden />
                    {badge > 0 && <span className={styles.dotBadge}>{badge > 99 ? '99+' : badge}</span>}
                  </span>
                  <span className={styles.sideLabel}>{a.label}</span>
                </Link>
                {active && a.items.length > 1 && (
                  <div className={styles.subLinks}>
                    {a.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={cx(styles.subLink, current?.item?.path === item.path && styles.subActive)}
                      >
                        {item.label}
                        {badges[item.path] > 0 && <span className={styles.countBadge}>{badges[item.path]}</span>}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <Link to="/settings" className={cx(styles.sideLink, styles.sideSettings, location.pathname === '/settings' && styles.sideActive)}>
          <span className={styles.sideIcon}><Settings aria-hidden /></span>
          <span className={styles.sideLabel}>Einstellungen</span>
        </Link>
      </aside>

      <div className={styles.main}>
        <header className={styles.topbar}>
          <div className={styles.topLeft}>
            {inAdminSubpage ? (
              <IconButton icon={ChevronLeft} label="Zurück zur Verwaltung" onClick={() => navigate(area.hub)} />
            ) : (
              <Link to="/" className={styles.mobileLogo} aria-label="Startseite"><span aria-hidden>🔧</span></Link>
            )}
            <h1 className={styles.title}>{title}</h1>
          </div>
          <div className={styles.topRight}>
            {!isOnline && (
              <span className={styles.offline} role="status"><WifiOff aria-hidden /> Offline</span>
            )}
            <button type="button" className={styles.searchButton} onClick={() => setSearchOpen(true)} aria-label="Suchen (Strg+K)">
              <Search aria-hidden />
              <span className={styles.searchText}>Suchen …</span>
              <kbd className={styles.searchKbd}>Strg K</kbd>
            </button>
            <Menu>
              <MenuButton className={styles.avatar} aria-label="Profil und Einstellungen">{initials(user)}</MenuButton>
              <MenuItems anchor="bottom end" className={styles.profileMenu}>
                <div className={styles.profileHead}>
                  <strong>{displayName(user)}</strong>
                  <span>{ROLE_LABELS[user?.permissions] || user?.permissions}</span>
                </div>
                <MenuSeparator className={styles.separator} />
                <MenuItem><Link to="/settings" className={styles.menuItem}><Settings aria-hidden /> Einstellungen</Link></MenuItem>
                <MenuItem><button type="button" className={styles.menuItem} onClick={startTour}><GraduationCap aria-hidden /> Einführung ansehen</button></MenuItem>
                <MenuItem><button type="button" className={styles.menuItem} onClick={onFeedback}><MessageSquare aria-hidden /> Feedback geben</button></MenuItem>
                <MenuItem><button type="button" className={styles.menuItem} onClick={onSuggestion}><MessageSquarePlus aria-hidden /> Doku-Vorschlag</button></MenuItem>
                <MenuSeparator className={styles.separator} />
                <MenuItem><button type="button" className={cx(styles.menuItem, styles.danger)} onClick={onLogout}><LogOut aria-hidden /> Abmelden</button></MenuItem>
                {appVersion && <p className={styles.version}>Version {appVersion}</p>}
              </MenuItems>
            </Menu>
          </div>
        </header>

        {statusBar}
        {devNotice}

        {showTabs && (
          <div className={styles.tabs}>
            <Tabs
              label={area.label}
              value={current.item.path}
              onChange={(path) => navigate(path)}
              items={area.items.map((item) => ({
                value: item.path,
                label: item.label,
                icon: iconFor(item.icon),
                count: badges[item.path] || undefined
              }))}
            />
          </div>
        )}

        <main className={styles.content} id="main">
          <PageTip pathname={location.pathname} icon={Lightbulb} />
          {children}
        </main>

        <footer className={styles.footer}>
          {footerExtra}
          <span>
            <span onClick={onCopyrightClick}>©</span> {new Date().getFullYear()}{' '}
            <span onClick={onBrandClick}>Lars Jänsch</span>
          </span>
          <a href="https://bentomax.de/impressum" target="_blank" rel="noopener noreferrer">Impressum</a>
          <Link to="/datenschutz">Datenschutz</Link>
          {appVersion && <span>v{appVersion}</span>}
        </footer>
      </div>

      <nav className={styles.bottomNav} aria-label="Hauptnavigation">
        {navigation.map((a) => {
          const Icon = iconFor(a.icon);
          const active = a.key === area?.key;
          const badge = areaBadge(a);
          return (
            <Link key={a.key} to={a.path} className={cx(styles.bottomLink, active && styles.bottomActive)} aria-current={active ? 'page' : undefined}>
              <span className={styles.bottomIcon}>
                <Icon aria-hidden />
                {badge > 0 && <span className={styles.dotBadge}>{badge > 99 ? '99+' : badge}</span>}
              </span>
              <span className={styles.bottomLabel}>{a.label}</span>
            </Link>
          );
        })}
      </nav>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} navigation={navigation} />
    </div>
  );
}
